/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import {
  ListChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import { css, jsx } from '@emotion/react';
import { Card, CardContent, Grid, Icon, Typography } from '@mui/material';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { TaskCardLocalizationList } from '../../constants/changes';

/**
 * The internal dependencies
 */
import TaskCardIconMap from '../../utils/changes';
import { formatNumber } from '../../utils/getTotalTime';
import { useProjectStore } from '../../store/projectStore';

export type TaskCardProps = {
  change: ListChangeDto;
  onClick: (changeId: number, status: string) => void;
};

const TaskCard = observer(({ change, onClick }: TaskCardProps) => {
  const TaskCardLocalization = TaskCardLocalizationList();

  const {
    id,
    title,
    status,
    implementationTargetDate,
    changeNoClient,
    totalSalesValue,
    submissionCRQuotationToClient,
    evaluationTargetDate,
  } = change;

  const { projects } = useProjectStore();

  const isOverdue = (): boolean => {
    const now = new Date().getTime();

    // Check for Evaluation-related statuses
    if (
      (status === UpdateChangeDtoStatusEnum.InEvaluation ||
        status === UpdateChangeDtoStatusEnum.EvalRework) &&
      evaluationTargetDate
    ) {
      const evaluationDate = new Date(evaluationTargetDate);
      return evaluationDate.getTime() < now;
    }

    // Check for Implementation-related statuses
    if (
      (status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
        status ===
          UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress) &&
      implementationTargetDate
    ) {
      const implementationDate = new Date(implementationTargetDate);
      return implementationDate.getTime() < now;
    }

    return false;
  };

  const getStatusText = (): string =>
    TaskCardLocalization[status as UpdateChangeDtoStatusEnum];

  const getFormattedDate = (
    dateString: string | null | undefined,
    color?: string,
  ): React.ReactNode => {
    if (!dateString) {
      return (
        <Typography
          variant="body2"
          component="span"
          sx={{ fontSize: '0.8rem', color: '#c42626ff' }}
        >
          {t('change.notSubmitted')}
        </Typography>
      );
    }
    const date = new Date(dateString);

    return (
      <Typography
        variant="body2"
        component="span"
        sx={{ fontSize: '0.8rem', color: color || '#5a5a5a' }}
      >
        {date.toLocaleDateString('de-DE', { timeZone: 'UTC' })}
      </Typography>
    );
  };

  return (
    <Card
      onClick={() => onClick(id, status ?? '')}
      variant="outlined"
      sx={{
        backgroundColor: isOverdue() ? '#FFD5D5' : 'white',
        '&:hover': {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        },
        maxWidth: 350,
        minWidth: 350,
        width: 350,
      }}
      css={css`
        padding: 16px;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
        box-shadow: none;
        transition: box-shadow 0.3s ease-in-out;
        cursor: pointer;
      `}
    >
      <CardContent
        css={css`
          padding: 0 !important;
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        {/* Top section: Title and Status */}
        <Grid
          container
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="nowrap"
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              wordBreak: 'break-word',
              lineHeight: 1.2,
              color: '#00223B',
              flexGrow: 1, // Add this to let the title take up the space
            }}
          >
            {title}
          </Typography>
          <Grid
            css={css`
              display: flex;
              align-items: center;
              gap: 4px;
              flex-shrink: 0; // Ensures the status component does not shrink
              margin-left: 8px;
              white-space: nowrap; // Prevents the status text from wrapping
            `}
          >
            <Icon fontSize="small">
              {TaskCardIconMap[status as UpdateChangeDtoStatusEnum]}
            </Icon>
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
              {getStatusText()}
            </Typography>
          </Grid>
        </Grid>

        {/* Middle section: Project abbreviation, ChangeNoClient, Author */}
        <Grid
          container
          direction="column"
          css={css`
            color: #757575;
            font-size: 0.8rem;
            gap: 4px;
          `}
        >
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <span
              style={{
                fontWeight: 500,
              }}
            >
              {t('project.projectShortCut')}:
            </span>{' '}
            {projects.find(proj => proj.id === change.projectId)?.code || '-'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            <span
              style={{
                fontWeight: 500,
              }}
            >
              {t('change.changeNoClient')}:
            </span>
            {changeNoClient}
          </Typography>
        </Grid>

        <Grid
          container
          direction="column"
          justifyContent="flex-end"
          css={css`
            font-size: 0.75rem;
            color: #5a5a5a;
            margin-top: 8px;
            gap: 4px;
          `}
        >
          <Typography variant="caption" component="p">
            {t('change.evaluateUntil')}:{' '}
            {evaluationTargetDate
              ? new Date(evaluationTargetDate).toLocaleDateString('de-DE')
              : '-'}
          </Typography>

          <Grid container alignItems="center">
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 500,
                marginRight: 1,
              }}
            >
              {t('dates.externalApprovalCustomerDate')}:
            </Typography>
            {getFormattedDate(submissionCRQuotationToClient, 'green')}
          </Grid>

          <Typography variant="caption" component="p">
            {t('change.implementUntil')}:{' '}
            {implementationTargetDate
              ? new Date(implementationTargetDate).toLocaleDateString('de-DE')
              : '-'}
          </Typography>

          {/* Always display Total Sales Value */}
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', fontWeight: 500 }}
          >
            {t('costs.totalSalesValue')}:{' '}
            {totalSalesValue ? `€ ${formatNumber(totalSalesValue)}` : '-'}
          </Typography>
        </Grid>
      </CardContent>
    </Card>
  );
});

export default TaskCard;
